/**
 * Hook to register a JSX instruction block with the instruction store.
 *
 * Renders the JSX tree to a single string via the instruction reconciler
 * and registers it as one block. Priority and placement are block-level
 * (not per-element) to guarantee stable ordering.
 *
 * @example
 * ```tsx
 * function MyInstructions() {
 *   useRegisterInstructions(
 *     <>
 *       <tag-identity>You are Atlas</tag-identity>
 *       <p>Be helpful.</p>
 *     </>,
 *     { priority: 10 },
 *   );
 *   return null;
 * }
 * ```
 */

import type { ReactNode } from 'react';
import { use, useEffect } from 'react';

import { renderInstructions } from './reconciler.ts';
import { InstructionStoreContext } from '#plugin-chat/contexts/InstructionStoreContext.ts';
import type { Placement } from '#plugin-chat/lib/instructions/types.ts';

/**
 * Registers a JSX instruction block with the instruction store.
 *
 * The JSX tree is rendered to a single string in document order (no per-tag
 * priority). Priority and placement are block-level properties passed via
 * `options` — all content inside the block shares them.
 *
 * Write-only by design: the hook pushes its rendered text into the store but
 * never subscribes, so registering one block never re-renders the others. The
 * single effect is the irreducible part — it defers the store write past commit
 * (writing during render would update subscribed readers mid-render) and
 * unregisters the block on unmount.
 *
 * @param element - JSX content (any mix of text, lists, `<tag-*>`, etc.)
 * @param options - `priority` (default 50) and `placement` (default 'system')
 */
export function useRegisterInstructions(
  element: ReactNode,
  { priority = 50, placement = 'system' }: { priority?: number; placement?: Placement } = {},
): void {
  const store = use(InstructionStoreContext);
  const content = renderInstructions(element);

  useEffect(
    () => (content ? store.getState().register({ priority, placement, content }) : undefined),
    [store, content, priority, placement],
  );
}
